// ProfitMigo calculation engine (frontend, instant client-side calc).
// Mirrors backend/calc_engine.py 1:1 — keep formulas in sync.
// Pure functions, no side effects.

const safeDiv = (a, b) => (b ? a / b : 0);

export function profitMargin(cost, sellingPrice) {
  const grossProfit = sellingPrice - cost;
  return {
    grossProfit,
    marginPct: safeDiv(grossProfit, sellingPrice) * 100,
    markupPct: safeDiv(grossProfit, cost) * 100,
    roiPct: safeDiv(grossProfit, cost) * 100,
  };
}

export function markup(cost, markupPct) {
  const sellingPrice = cost * (1 + markupPct / 100);
  const grossProfit = sellingPrice - cost;
  return { sellingPrice, grossProfit, marginPct: safeDiv(grossProfit, sellingPrice) * 100 };
}

export function roi(initialInvestment, grossReturn, additionalCosts = 0) {
  const totalInvested = initialInvestment + additionalCosts;
  const netReturn = grossReturn - totalInvested;
  return { returnAmount: netReturn, roiPct: safeDiv(netReturn, totalInvested) * 100, totalInvested };
}

export function breakEven(fixedCosts, variableCostPerUnit, sellingPrice) {
  const contribution = sellingPrice - variableCostPerUnit;
  const units = contribution > 0 ? safeDiv(fixedCosts, contribution) : null;
  return {
    contributionPerUnit: contribution,
    breakEvenUnits: units,
    breakEvenRevenue: units !== null ? units * sellingPrice : null,
  };
}

export function sellingPrice(cost, desiredMarginPct, feePct = 0, fixedCosts = 0) {
  const denom = 1 - (desiredMarginPct + feePct) / 100;
  return { requiredSellingPrice: denom > 0 ? safeDiv(cost + fixedCosts, denom) : null };
}

// Full per-order profitability (Product / Amazon / Meesho / Marketplace-fee).
export function detailedProfit({
  sellingPrice = 0,
  productCost = 0,
  shippingCost = 0,
  packagingCost = 0,
  commissionPct = 0,
  fixedFee = 0,
  paymentPct = 0,
  gstPct = 0,
  adCost = 0,
  discount = 0,
  returnRatePct = 0,
  otherCosts = 0,
}) {
  const netPrice = sellingPrice - discount;
  const commission = (netPrice * commissionPct) / 100;
  const payment = (netPrice * paymentPct) / 100;
  const gst = (netPrice * gstPct) / 100;
  const expectedReturnCost = (returnRatePct / 100) * (shippingCost + packagingCost);

  const revenue = netPrice;
  const feeCosts = commission + payment + gst + fixedFee;
  const fixedCosts = productCost + shippingCost + packagingCost + adCost + expectedReturnCost + otherCosts;
  const totalCosts = feeCosts + fixedCosts;

  const grossProfit = revenue - productCost;
  const netProfit = revenue - totalCosts;
  const marginPct = safeDiv(netProfit, revenue) * 100;
  const roiPct = safeDiv(netProfit, totalCosts) * 100;

  const k = (commissionPct + paymentPct + gstPct) / 100;
  const nonPriceFixed = fixedCosts + fixedFee;
  const beNetPrice = 1 - k > 0 ? safeDiv(nonPriceFixed, 1 - k) : null;
  const breakEvenPrice = beNetPrice !== null ? beNetPrice + discount : null;
  const maxAdCost = netProfit + adCost;

  return {
    revenue, totalCosts, feeCosts, commission, paymentFee: payment, gst,
    expectedReturnCost, grossProfit, netProfit, marginPct, roiPct,
    breakEvenPrice, maxAdCost,
  };
}

export function codRtoProfit({
  orders = 0,
  sellingPrice = 0,
  productCost = 0,
  shippingPerOrder = 0,
  packagingCost = 0,
  codFeePct = 0,
  rtoRatePct = 0,
  rtoShippingCost = 0,
  adCostPerOrder = 0,
  otherPerOrder = 0,
}) {
  const delivered = orders * (1 - rtoRatePct / 100);
  const rtoOrders = orders * (rtoRatePct / 100);
  const codFee = (sellingPrice * codFeePct) / 100;
  const perDeliveredCost = productCost + shippingPerOrder + packagingCost + codFee + adCostPerOrder + otherPerOrder;
  const perRtoLoss = rtoShippingCost + packagingCost + adCostPerOrder;

  const totalRevenue = delivered * sellingPrice;
  const totalDeliveredCosts = delivered * perDeliveredCost;
  const totalRtoLoss = rtoOrders * perRtoLoss;
  const netProfit = totalRevenue - totalDeliveredCosts - totalRtoLoss;

  return {
    deliveredOrders: delivered,
    rtoOrders,
    totalRevenue,
    totalDeliveredCosts,
    totalRtoLoss,
    netProfit,
    profitPerDeliveredOrder: safeDiv(netProfit, delivered),
    marginPct: safeDiv(netProfit, totalRevenue) * 100,
  };
}

// Dispatch by calculator engine type.
export function computeByType(type, values) {
  if (type === "cod") return codRtoProfit(values);
  return detailedProfit(values);
}
